// pages/Yourlostfound.js
import React, { useState, useRef, useEffect } from "react";
import { getFullImageUrl } from "../../middleware/Imagegetter";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Grid,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Backdrop,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Checkbox,
  Fade,
  Zoom,
  Slide,
  Grow,
  Avatar,
  Badge,
  Stack,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Search,
  AddCircle,
  Close,
  Sort,
  Share,
  Favorite,
  FavoriteBorder,
  LocationOn,
  Email,
  Image,
  Refresh,
  CloudUpload,
  CameraAlt,
  AutoAwesome,
  ArrowBack,
  CardGiftcard,
  CheckCircle,
  Error,
  Info,
  Warning,
  Dashboard,
  Groups,
  Event,
  Person,
  School,
  Settings,
  Help,
  Lightbulb,
  Notifications,
  Menu as MenuIcon,
  ChevronRight,
  ArrowForward,
  TrendingUp,
  MoreVert,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PowerSettingsNew as DeactivateIcon,
  Add as AddIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../api";

import LogoutNavbar from "../../components/LogoutNavbar";
import PostLoginFooter from "../../components/PostLoginFooter";
import GlobalSidebar from "../../components/GlobalSidebar";
import BackButton from "../../components/BackButton";

// Import icons for sidebar
import {
  AccountCircle as ProfileIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";

// Theme based on PeerCampus Admin Dashboard (matching LostFound.jsx)
const peerCampusTheme = {
  primary: "#0d2a44",       // Deep blue
  secondary: "#016f57",     // Teal green
  accent: "#F59E0B",        // Amber
  background: "#F8FAFC",    // Light slate background
  surface: "#FFFFFF",       // White cards/surfaces
  text: "#1E293B",          // Slate 800
  textSecondary: "#64748B", // Slate 500
  border: "#E2E8F0",        // Slate 200
  success: "#016f57",
  warning: "#F59E0B",
  error: "#EF4444",
  info: "#3B82F6",
  sidebar: "#1E293B",       // Dark sidebar
};

const peerCampusGradient =
  "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)";

// Image styling constants for consistency
const imageStyles = {
  container: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F1F5F9',
    borderRadius: '12px',
    p: 1
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    maxHeight: '400px'
  },
  cardImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    backgroundColor: '#F1F5F9',
    p: 1
  }
};

// Enhanced notification system (matching LostFound.jsx)
const EnhancedAlert = ({ severity, message, onClose, autoHide = true }) => (
  <motion.div
    initial={{ opacity: 0, x: 300, scale: 0.8 }}
    animate={{ opacity: 1, x: 0, scale: 1 }}
    exit={{ opacity: 0, x: 300, scale: 0.8 }}
    transition={{ type: "spring", stiffness: 500, damping: 30 }}
  >
    <Alert 
      severity={severity} 
      variant="filled" 
      sx={{ 
        fontWeight: "bold",
        width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
        maxWidth: { xs: 'calc(100vw - 32px)', sm: 420 },
        wordBreak: 'break-word',
        borderRadius: 3,
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        backdropFilter: "blur(10px)",
        background: severity === "error" ? 
          `linear-gradient(135deg, #EF4444, #DC2626)` :
          severity === "warning" ?
          `linear-gradient(135deg, #F59E0B, #EAB308)` :
          severity === "info" ?
          peerCampusGradient :
          peerCampusGradient
      }}
      onClose={onClose}
      iconMapping={{
        success: <CheckCircle sx={{ fontSize: 24 }} />,
        error: <Error sx={{ fontSize: 24 }} />,
        warning: <Warning sx={{ fontSize: 24 }} />,
        info: <Info sx={{ fontSize: 24 }} />,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {message}
      </motion.div>
    </Alert>
  </motion.div>
);

// Reusable inline loader component (matching LostFound.jsx)
const InlineLoader = ({ size = 24, color = peerCampusTheme.primary }) => {
  const loaderSize = typeof size === 'number' ? size : 24;
  
  return (
    <Box sx={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      position: 'relative',
      width: loaderSize,
      height: loaderSize
    }}>
      {/* Static background circle */}
      <Box sx={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        border: `2px solid ${color}20`
      }} />
      
      {/* Animated arc */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ 
          duration: 1, 
          repeat: Infinity, 
          ease: "linear" 
        }}
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          border: `2px solid transparent`,
          borderTop: `2px solid ${color}`,
          borderRight: `2px solid ${color}80`
        }}
      />
    </Box>
  );
};

// Modern Smooth Loading Overlay (matching LostFound.jsx)
const LoadingOverlay = ({ message = "Loading your items...", subMessage = "Please wait" }) => (
  <Backdrop
    sx={{ 
      color: '#fff', 
      zIndex: (theme) => theme.zIndex.drawer + 1,
      background: `linear-gradient(135deg, ${peerCampusTheme.background}EE, ${peerCampusTheme.surface}EE)`,
      backdropFilter: "blur(10px)",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}
    open={true}
  >
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{ 
        textAlign: 'center',
        maxWidth: '400px',
        width: '90%'
      }}
    >
      {/* Modern Loader Animation */}
      <Box sx={{ position: 'relative', width: 100, height: 100, mx: 'auto', mb: 4 }}>
        {/* Outer ring */}
        <motion.div
          animate={{ 
            rotate: 360,
            borderWidth: [3, 4, 3],
            opacity: [0.7, 1, 0.7]
          }}
          transition={{ 
            rotate: { duration: 1.5, repeat: Infinity, ease: "linear" },
            borderWidth: { duration: 1.5, repeat: Infinity },
            opacity: { duration: 1.5, repeat: Infinity }
          }}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: `3px solid ${peerCampusTheme.primary}30`,
            borderTop: `3px solid ${peerCampusTheme.primary}`,
            borderRight: `3px solid ${peerCampusTheme.primary}`,
          }}
        />
        
        {/* Inner ring */}
        <motion.div
          animate={{ 
            rotate: -360,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            rotate: { duration: 2, repeat: Infinity, ease: "linear" },
            scale: { duration: 1.5, repeat: Infinity }
          }}
          style={{
            position: 'absolute',
            top: '20%',
            left: '20%',
            width: '60%',
            height: '60%',
            borderRadius: '50%',
            border: `2px solid ${peerCampusTheme.secondary}30`,
            borderBottom: `2px solid ${peerCampusTheme.secondary}`,
            borderLeft: `2px solid ${peerCampusTheme.secondary}`,
          }}
        />
        
        {/* Center dot */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            backgroundColor: [peerCampusTheme.primary, peerCampusTheme.secondary, peerCampusTheme.primary]
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity
          }}
          style={{
            position: 'absolute',
            top: '40%',
            left: '40%',
            width: '20%',
            height: '20%',
            borderRadius: '50%',
            backgroundColor: peerCampusTheme.primary,
          }}
        />
      </Box>
      
      {/* Text Content */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Typography variant="h5" gutterBottom sx={{ 
          fontWeight: 'bold',
          background: peerCampusGradient,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          mb: 1
        }}>
          {message}
        </Typography>
        
        <Typography variant="body1" color={peerCampusTheme.textSecondary} sx={{ mb: 3 }}>
          {subMessage}
        </Typography>
      </motion.div>

      {/* Progress dots */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        {[0, 1, 2].map((index) => (
          <motion.div
            key={index}
            animate={{ 
              y: [0, -8, 0],
              scale: [1, 1.2, 1]
            }}
            transition={{ 
              duration: 1.2,
              repeat: Infinity,
              delay: index * 0.2,
              ease: "easeInOut"
            }}
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: peerCampusGradient,
            }}
          />
        ))}
      </Box>
    </motion.div>
  </Backdrop>
);

// Stats Card Component (matching LostFound.jsx)
const StatsCard = ({ title, value, icon, color, trend }) => (
  <Card sx={{ 
    p: 3, 
    borderRadius: 2,
    border: `1px solid ${peerCampusTheme.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Box sx={{ 
        p: 1.5, 
        borderRadius: 2,
        background: `${color}10`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {React.cloneElement(icon, { sx: { color: color, fontSize: 24 } })}
      </Box>
      {trend && (
        <Chip 
          label={trend} 
          size="small" 
          color={trend.includes('+') ? "success" : "error"}
          sx={{ fontSize: '0.7rem', height: 24 }}
        />
      )}
    </Box>
    <Typography variant="h4" fontWeight="bold" color={peerCampusTheme.text} gutterBottom>
      {value}
    </Typography>
    <Typography variant="body2" color={peerCampusTheme.textSecondary}>
      {title}
    </Typography>
  </Card>
);

// Section Header Component (matching LostFound.jsx)
const SectionHeader = ({ title, subtitle, action }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
      <Box>
        <Typography variant="h5" fontWeight="bold" color={peerCampusTheme.text}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color={peerCampusTheme.textSecondary}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && (
        <Button
          variant="outlined"
          size="small"
          endIcon={<ChevronRight />}
          sx={{ 
            borderColor: peerCampusTheme.border,
            color: peerCampusTheme.text,
            '&:hover': {
              borderColor: peerCampusTheme.primary,
              backgroundColor: `${peerCampusTheme.primary}05`
            }
          }}
        >
          {action}
        </Button>
      )}
    </Box>
    <Divider sx={{ borderColor: peerCampusTheme.border }} />
  </Box>
);

export default function YourLostFound({ embedded = false } = {}) {
  const footerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [items, setItems] = useState([]);
  const [viewMode, setViewMode] = useState("lost"); // "lost" or "found"
  const [selectedItem, setSelectedItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deactivateConfirm, setDeactivateConfirm] = useState(null);
  const [deactivateSuccess, setDeactivateSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [displayMode, setDisplayMode] = useState("grid"); // "grid" or "list"
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    location: "",
    category: "",
    urgency: "medium",
    reward: false,
    rewardDetails: ""
  });

  // Notification state
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // User state
  const [currentUser, setUser] = useState({ id: null });

  // Define sidebar items
  const sidebarItems = [
    { 
      label: 'Dashboard', 
      path: '/login/student/home', 
      action: () => navigate('/login/student/home'),
      icon: <Dashboard />
    },
    { 
      label: 'Lost & Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Events', 
      path: '/events', 
      action: () => navigate('/events'),
      icon: <Event />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <Lightbulb />
    },
    { 
      label: 'Doubts', 
      path: '/yourdoubts', 
      action: () => navigate('/yourdoubts'),
      icon: <Help />
    },
    { 
      label: 'My Items', 
      path: '/yourlostfound', 
      action: () => navigate('/yourlostfound'),
      icon: <LostFoundIcon />,
      active: true
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      action: () => navigate('/settings'),
      icon: <Settings />
    },
  ];

  // Enhanced notification handler
  const showNotification = (message, type = "success", duration = 4000) => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, duration);
  };

  // Enhanced success handler
  const showSuccessMessage = (message, actionText = null, action = null) => {
    setSuccessMessage(message);
    setShowSuccess(true);
  };

  // Fetch user details
  useEffect(() => {
    const fetchdetails = async () => {
      try {
        const response = await api.get("users/me/");
        // console.log("User details response:", response);
        setUser({
          id: response.id,
          email: response.email,
          name: response.name
        });
      } catch (error) {
        console.error("Error occurred:", error);
        showNotification("Failed to load user details", "error");
      }
    };
    fetchdetails();
  }, []);

  // Check if we're returning from adding an item
  useEffect(() => {
    const checkForNewItem = () => {
      const searchParams = new URLSearchParams(location.search);
      const newItemAdded = searchParams.get('newItemAdded');
      const itemType = searchParams.get('itemType');
      
      if (newItemAdded === 'true' && itemType) {
        fetchUserItems();
        setViewMode(itemType);
        showSuccessMessage(`Item ${itemType === 'lost' ? 'reported as lost' : 'added as found'} successfully!`);
        navigate('/yourlostfound', { replace: true });
      }
    };

    checkForNewItem();
  }, [location, navigate]);

  // Fetch user items
  const fetchUserItems = async () => {
    setLoading(true);
    
    try {
      const [lostResponse, foundResponse] = await Promise.all([
        api.get('lostfound/lost-items/'),
        api.get('lostfound/found-items/')
      ]);

      // Process lost items
      const lostData = lostResponse.data?.results || lostResponse.data || lostResponse.results || [];
      const userLostItems = lostData.filter(item => {
        const ownerId = item.owner?.id || item.owner;
        return ownerId == currentUser.id;
      });

      const formattedLostData = userLostItems.map(item => ({
        ...item,
        id: item.id || `lost-${Math.random()}`,
        type: "lost",
        title: item.title || "Untitled Lost Item",
        description: item.description || "No description provided",
        location: item.location || "Unknown location",
        image: getFullImageUrl(item.image),
        email: item.owner?.email || currentUser.email || "",
        category: item.category || "Other",
        urgency: item.urgency || "medium",
        reward: item.reward || false,
        rewardDetails: item.rewardDetails || "",
        status: item.status || "active",
        created_at: item.created_at || new Date().toISOString(),
        likes: item.likes || 0,
        matched: item.matched || false
      }));

      // Process found items
      const foundData = foundResponse.data?.results || foundResponse.data || foundResponse.results || [];
      const userFoundItems = foundData.filter(item => {
        const ownerId = item.owner?.id || item.owner;
        return ownerId == currentUser.id;
      });

      const formattedFoundData = userFoundItems.map(item => ({
        ...item,
        id: item.id || `found-${Math.random()}`,
        type: "found",
        title: item.title || "Untitled Found Item",
        description: item.description || "No description provided",
        location: item.location || "Unknown location",
        image: getFullImageUrl(item.image),
        email: item.owner?.email || currentUser.email || "",
        category: item.category || "Other",
        status: item.status || "active",
        created_at: item.created_at || new Date().toISOString(),
        likes: item.likes || 0,
        claimed: item.claimed || false
      }));

      const allItems = [...formattedLostData, ...formattedFoundData];
      setItems(allItems);

      if (allItems.length === 0) {
        showNotification("No items found. Start by adding your first item!", "info");
      } else {
        showNotification(`Loaded ${allItems.length} of your items`, "success");
      }

    } catch (error) {
      console.error("Error fetching user items:", error);
      
      if (error.response?.status === 401) {
        showNotification("Session expired. Please log in again.", "error");
      } else if (error.response?.status === 403) {
        showNotification("You don't have permission to view these items.", "error");
      } else if (error.response?.status >= 500) {
        showNotification("Server error. Please try again later.", "error");
      } else {
        showNotification("Failed to load your items. Please check your connection.", "error");
      }
      
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser.id !== null) {
      fetchUserItems();
    }
  }, [currentUser.id]);

  // Sort items by date (most recent first)
  const sortItemsByDate = (items) => {
    return [...items].sort((a, b) => {
      const dateA = new Date(a.created_at || a.date || 0);
      const dateB = new Date(b.created_at || b.date || 0);
      return dateB - dateA;
    });
  };

  // Filter and sort items
  const filteredItems = items
    .filter(item => item.type === viewMode)
    .filter(item =>
      (item.title?.toLowerCase().includes(search.toLowerCase()) ||
       item.description?.toLowerCase().includes(search.toLowerCase()) ||
       item.location?.toLowerCase().includes(search.toLowerCase()))
    )
    .sort((a, b) => {
      switch (sortBy) {
        case "newest": return new Date(b.created_at) - new Date(a.created_at);
        case "oldest": return new Date(a.created_at) - new Date(b.created_at);
        case "likes": return (b.likes || 0) - (a.likes || 0);
        case "title": return (a.title || "").localeCompare(b.title || "");
        default: return 0;
      }
    });

  // Handle item deletion
  const handleDeleteItem = async () => {
    if (!deleteConfirm) return;
    
    setLoading(true);
    
    try {
      const endpoint = deleteConfirm.type === "lost" 
        ? `lostfound/lost-items/${deleteConfirm.id}/`
        : `lostfound/found-items/${deleteConfirm.id}/`;
      
      await api.delete(endpoint);
      
      setItems(prev => prev.filter(item => item.id !== deleteConfirm.id));
      setDeleteConfirm(null);
      
      showNotification("Item deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting item:", error);
      showNotification("Error deleting item", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle item deactivation
  const handleDeactivateItem = async () => {
    if (!deactivateConfirm) return;
    
    setLoading(true);
    
    try {
      setDeactivateConfirm(null);
      
      setItems(prev => 
        prev.map(item => 
          item.id === deactivateConfirm.id 
            ? { ...item, status: "inactive" }
            : item
        )
      );
      
      setDeactivateSuccess(true);
      showNotification("Item deactivated successfully", "success");
    } catch (error) {
      console.error("Error deactivating item:", error);
      showNotification("Error deactivating item", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle item edit
  const handleEditItem = async () => {
    if (!editItem || !editForm.title.trim()) return;
    
    setLoading(true);
    
    try {
      const endpoint = editItem.type === "lost" 
        ? `lostfound/lost-items/${editItem.id}/`
        : `lostfound/found-items/${editItem.id}/`;
      
      const response = await api.put(endpoint, editForm);
      
      if (response.data) {
        setItems(prev => 
          prev.map(item => 
            item.id === editItem.id 
              ? { 
                  ...item, 
                  ...response.data, 
                  image: getFullImageUrl(response.data.image),
                  reward: editForm.reward,
                  rewardDetails: editForm.rewardDetails
                }
              : item
          )
        );
        setEditItem(null);
        setEditForm({ title: "", description: "", location: "", category: "", urgency: "medium", reward: false, rewardDetails: "" });
        
        showNotification("Item updated successfully", "success");
      }
    } catch (error) {
      console.error("Error updating item:", error);
      showNotification("Error updating item", "error");
    } finally {
      setLoading(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (item) => {
    setEditItem(item);
    setEditForm({
      title: item.title || "",
      description: item.description || "",
      location: item.location || "",
      category: item.category || "",
      urgency: item.urgency || "medium",
      reward: item.reward || false,
      rewardDetails: item.rewardDetails || ""
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown date";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };

  // Navigate to add lost item page with redirect back
  const handleAddLostItem = () => {
    navigate('/lostfound?redirect=yourlostfound&type=lost');
  };

  // Navigate to add found item page with redirect back
  const handleAddFoundItem = () => {
    navigate('/lostfound?redirect=yourlostfound&type=found');
  };

  // Get statistics
  const getStats = () => {
    const lostItems = items.filter(item => item.type === "lost");
    const foundItems = items.filter(item => item.type === "found");
    const activeLost = lostItems.filter(item => item.status !== "inactive").length;
    const activeFound = foundItems.filter(item => item.status !== "inactive").length;
    const matchedItems = lostItems.filter(item => item.matched).length + foundItems.filter(item => item.claimed).length;
    
    return {
      totalLost: lostItems.length,
      totalFound: foundItems.length,
      activeLost,
      activeFound,
      matched: matchedItems
    };
  };

  const stats = getStats();

  // Item Card Component (matching LostFound.jsx style)
  const ItemCard = ({ item }) => (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: -20 }}
      whileHover={{ 
        y: isMobile ? 0 : -4, 
        transition: { duration: 0.2, ease: "easeOut" }
      }}
    >
      <Card 
        sx={{ 
          background: peerCampusTheme.surface,
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${peerCampusTheme.border}`,
          cursor: 'pointer',
          height: '100%',
          transition: 'all 0.3s ease',
          position: 'relative',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }
        }}
        onClick={() => setSelectedItem(item)}
      >
        {/* Status Overlay */}
        {item.status === "inactive" && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2
            }}
          >
            <Chip 
              label="DEACTIVATED" 
              sx={{ 
                fontWeight: 'bold', 
                backgroundColor: peerCampusTheme.error,
                color: 'white'
              }}
            />
          </Box>
        )}

        <CardMedia
          component="img"
          height={isMobile ? 140 : 200}
          image={item.image || "/fallback-image.jpg"}
          alt={item.title}
          sx={{ 
            ...imageStyles.cardImg,
            height: isMobile ? 140 : 200,
            opacity: item.status === "inactive" ? 0.5 : 1
          }}
          onError={(e) => {
            e.target.src = "/fallback-image.jpg";
            e.target.style.objectFit = 'contain';
          }}
        />
        
        {/* Badges */}
        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5, flexDirection: 'column' }}>
          <Chip 
            label={item.type === "lost" ? "LOST" : "FOUND"} 
            size="small" 
            color={item.type === "lost" ? "error" : "success"}
            sx={{ fontSize: '0.65rem', height: 20, fontWeight: 'bold' }}
          />
          {item.type === "lost" && item.urgency === "high" && item.status !== "inactive" && (
            <Chip 
              label="URGENT" 
              size="small" 
              sx={{ 
                fontSize: '0.65rem', 
                height: 20,
                backgroundColor: peerCampusTheme.error,
                color: 'white'
              }}
            />
          )}
          {item.type === "lost" && item.reward && item.status !== "inactive" && (
            <Chip 
              label="REWARD" 
              size="small" 
              sx={{ 
                fontSize: '0.65rem', 
                height: 20,
                backgroundColor: peerCampusTheme.warning,
                color: 'white'
              }}
            />
          )}
          {item.type === "found" && item.claimed && item.status !== "inactive" && (
            <Chip 
              label="CLAIMED" 
              size="small" 
              sx={{ 
                fontSize: '0.65rem', 
                height: 20,
                backgroundColor: peerCampusTheme.success,
                color: 'white'
              }}
            />
          )}
        </Box>
        
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography 
              variant="subtitle1" 
              fontWeight="600" 
              color={peerCampusTheme.text} 
              gutterBottom
              sx={{ 
                fontSize: '0.95rem',
                lineHeight: 1.2
              }}
            >
              {item.title}
            </Typography>
          </Box>
          
          <Typography 
            variant="body2" 
            color={peerCampusTheme.textSecondary} 
            sx={{ 
              mb: 2, 
              height: 40, 
              overflow: 'hidden',
              fontSize: '0.85rem',
              lineHeight: 1.4
            }}
          >
            {item.description}
          </Typography>
          
          {/* Category */}
          <Chip 
            label={item.category || "Other"} 
            size="small" 
            variant="outlined"
            sx={{ 
              fontSize: '0.65rem',
              height: 20,
              mb: 1,
              borderColor: peerCampusTheme.primary,
              color: peerCampusTheme.primary
            }}
          />
          
          {/* Reward Display */}
          {item.reward && item.rewardDetails && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip 
                label={`Reward: ${item.rewardDetails}`} 
                size="small" 
                sx={{ 
                  fontSize: '0.65rem',
                  height: 20,
                  backgroundColor: `${peerCampusTheme.warning}15`,
                  color: peerCampusTheme.warning
                }}
              />
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LocationOn sx={{ fontSize: 14, mr: 0.5, color: peerCampusTheme.textSecondary }} />
            <Typography 
              variant="caption" 
              color={peerCampusTheme.textSecondary}
              sx={{ fontSize: '0.75rem' }}
            >
              {item.location}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Email sx={{ fontSize: 14, mr: 0.5, color: peerCampusTheme.textSecondary }} />
            <Typography 
              variant="caption" 
              color={peerCampusTheme.textSecondary}
              sx={{ 
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {item.email}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FavoriteBorder sx={{ fontSize: 16, color: peerCampusTheme.textSecondary }} />
              <Typography 
                variant="caption" 
                color={peerCampusTheme.textSecondary}
                sx={{ fontSize: '0.75rem' }}
              >
                {item.likes || 0}
              </Typography>
            </Box>
            
            <Typography 
              variant="caption" 
              color={peerCampusTheme.textSecondary}
              sx={{ fontSize: '0.75rem' }}
            >
              {formatDate(item.created_at)}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 0.5,
            mt: 2,
            pt: 1,
            borderTop: `1px solid ${peerCampusTheme.border}`
          }}>
            <Tooltip title="Edit">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  openEditDialog(item);
                }}
                sx={{ color: peerCampusTheme.primary }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {item.status !== "inactive" && (
              <Tooltip title="Deactivate">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeactivateConfirm(item);
                  }}
                  sx={{ color: peerCampusTheme.error }}
                >
                  <DeactivateIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            <Tooltip title="Delete">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirm(item);
                }}
                sx={{ color: peerCampusTheme.textSecondary }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  // List View Item Component
  const ListViewItem = ({ item }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
    >
      <Paper 
        sx={{ 
          p: 2, 
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${peerCampusTheme.border}`,
          cursor: 'pointer',
          opacity: item.status === "inactive" ? 0.6 : 1,
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: peerCampusTheme.primary,
            boxShadow: `0 4px 12px ${peerCampusTheme.primary}20`
          }
        }}
        onClick={() => setSelectedItem(item)}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={2}>
            <Box
              component="img"
              src={item.image || "/fallback-image.jpg"}
              alt={item.title}
              sx={{
                width: '100%',
                height: 80,
                objectFit: 'cover',
                borderRadius: 1,
                backgroundColor: peerCampusTheme.background
              }}
              onError={(e) => {
                e.target.src = "/fallback-image.jpg";
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle1" fontWeight="600" color={peerCampusTheme.text}>
              {item.title}
            </Typography>
            <Typography variant="caption" color={peerCampusTheme.textSecondary}>
              {formatDate(item.created_at)}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOn sx={{ fontSize: 16, color: peerCampusTheme.accent }} />
              <Typography variant="body2" color={peerCampusTheme.textSecondary}>
                {item.location}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={2}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              <Chip 
                label={item.type.toUpperCase()} 
                size="small" 
                color={item.type === "lost" ? "error" : "success"}
                sx={{ fontSize: '0.7rem' }}
              />
              {item.status === "inactive" && (
                <Chip 
                  label="INACTIVE" 
                  size="small" 
                  color="default"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Stack>
          </Grid>
          
          <Grid item xs={12} sm={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Tooltip title="Edit">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditDialog(item);
                  }}
                  sx={{ color: peerCampusTheme.primary }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              {item.status !== "inactive" && (
                <Tooltip title="Deactivate">
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeactivateConfirm(item);
                    }}
                    sx={{ color: peerCampusTheme.error }}
                  >
                    <DeactivateIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              
              <Tooltip title="Delete">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirm(item);
                  }}
                  sx={{ color: peerCampusTheme.textSecondary }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </motion.div>
  );

  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column", 
      minHeight: embedded ? "auto" : "100vh", 
      bgcolor: peerCampusTheme.background,
      color: peerCampusTheme.text,
    }}>
      {/* Navbar */}
      {!embedded && <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />}

      {/* Loading Overlay */}
      {loading && <LoadingOverlay 
        message="Processing your request..." 
        subMessage="Please wait while we update your items"
      />}

      <Box sx={{ display: "flex", flexGrow: 1 }}>
        {/* Sidebar */}
        {!embedded && !isMobile && <GlobalSidebar />}
        
        {/* Main Content */}
        <Box component="main" sx={{ 
          flexGrow: 1, 
          p: embedded ? 0 : { xs: 2, sm: 3, md: 4 },
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          {!embedded && <BackButton sx={{ mb: 2 , backgroundColor: "#dfdfdff", color: "#000" }} />}
          <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 1, md: 2 } }}>
            {/* Welcome Header */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                fontWeight="bold" 
                color={peerCampusTheme.text}
                sx={{ mb: 1 }}
              >
                Your Lost & Found Items
              </Typography>
              <Typography 
                variant="body1" 
                color={peerCampusTheme.textSecondary}
              >
                Manage all your lost and found items in one place
              </Typography>
            </Box>

            {/* Stats Section */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={6} sm={3}>
                <StatsCard 
                  title="Total Lost" 
                  value={stats.totalLost} 
                  icon={<Search />} 
                  color={peerCampusTheme.error}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatsCard 
                  title="Total Found" 
                  value={stats.totalFound} 
                  icon={<CheckCircle />} 
                  color={peerCampusTheme.success}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatsCard 
                  title="Active Items" 
                  value={stats.activeLost + stats.activeFound} 
                  icon={<AutoAwesome />} 
                  color={peerCampusTheme.primary}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatsCard 
                  title="Matched/Claimed" 
                  value={stats.matched} 
                  icon={<TrendingUp />} 
                  color={peerCampusTheme.warning}
                />
              </Grid>
            </Grid>

            {/* Header Section with Actions */}
            <Paper sx={{ 
              p: 3, 
              mb: 4, 
              borderRadius: 2,
              border: `1px solid ${peerCampusTheme.border}`
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2
              }}>
                <Box>
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Typography variant="body2" color={peerCampusTheme.textSecondary}>
                    Add new items or manage your existing listings
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddLostItem}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      background: peerCampusGradient,
                      '&:hover': {
                        background: peerCampusGradient,
                      }
                    }}
                  >
                    Report Lost Item
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddFoundItem}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      background: peerCampusGradient,
                      '&:hover': {
                        background: peerCampusGradient,
                      }
                    }}
                  >
                    Add Found Item
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* View Mode Toggle and Filters */}
            <Paper sx={{ 
              p: 2, 
              mb: 4, 
              borderRadius: 2,
              border: `1px solid ${peerCampusTheme.border}`
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2
              }}>
                {/* View Type Toggle */}
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      borderColor: peerCampusTheme.border,
                      color: peerCampusTheme.textSecondary,
                      '&.Mui-selected': {
                        background: peerCampusGradient,
                        color: 'white',
                        '&:hover': {
                          background: peerCampusGradient,
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="lost">
                    Lost Items ({items.filter(item => item.type === "lost").length})
                  </ToggleButton>
                  <ToggleButton value="found">
                    Found Items ({items.filter(item => item.type === "found").length})
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* Search and Display Controls */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  <TextField
                    placeholder={`Search ${viewMode} items...`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    sx={{ 
                      minWidth: { xs: '100%', sm: 250 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: peerCampusTheme.surface
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: peerCampusTheme.textSecondary, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <TextField
                    select
                    size="small"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    sx={{ 
                      minWidth: 120,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: peerCampusTheme.surface
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Sort sx={{ color: peerCampusTheme.textSecondary, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="newest">Newest First</MenuItem>
                    <MenuItem value="oldest">Oldest First</MenuItem>
                    <MenuItem value="likes">Most Liked</MenuItem>
                    <MenuItem value="title">Title A-Z</MenuItem>
                  </TextField>
                  
                  <ToggleButtonGroup
                    value={displayMode}
                    exclusive
                    onChange={(e, newMode) => newMode && setDisplayMode(newMode)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        borderColor: peerCampusTheme.border,
                        color: peerCampusTheme.textSecondary,
                        '&.Mui-selected': {
                          background: peerCampusGradient,
                          color: 'white',
                          '&:hover': {
                            background: peerCampusGradient,
                          }
                        }
                      }
                    }}
                  >
                    <ToggleButton value="grid">
                      <GridViewIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="list">
                      <ViewListIcon fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
            </Paper>

            {/* Items Display */}
            <AnimatePresence mode="wait">
              {filteredItems.length > 0 ? (
                displayMode === "grid" ? (
                  <Grid container spacing={2}>
                    {filteredItems.map((item) => (
                      <Grid 
                        item 
                        xs={12} 
                        sm={6} 
                        md={4} 
                        lg={3}
                        key={`${item.type}-${item.id}`}
                      >
                        <ItemCard item={item} />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box>
                    {filteredItems.map((item) => (
                      <ListViewItem key={`${item.type}-${item.id}`} item={item} />
                    ))}
                  </Box>
                )
              ) : (
                <Paper
                  sx={{
                    p: 6,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: `1px solid ${peerCampusTheme.border}`,
                    bgcolor: peerCampusTheme.surface
                  }}
                >
                  <Search sx={{ fontSize: 64, color: peerCampusTheme.textSecondary, mb: 2 }} />
                  <Typography variant="h6" gutterBottom color={peerCampusTheme.text}>
                    No {viewMode} items found
                  </Typography>
                  <Typography variant="body2" color={peerCampusTheme.textSecondary} sx={{ mb: 3 }}>
                    {search 
                      ? "Try adjusting your search or filters"
                      : viewMode === "lost" 
                        ? "You haven't reported any lost items yet." 
                        : "You haven't added any found items yet."}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={viewMode === "lost" ? handleAddLostItem : handleAddFoundItem}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 4,
                      background: peerCampusGradient,
                      '&:hover': {
                        background: peerCampusGradient,
                      }
                    }}
                  >
                    {viewMode === "lost" ? "Report Lost Item" : "Add Found Item"}
                  </Button>
                </Paper>
              )}
            </AnimatePresence>
          </Container>
        </Box>
      </Box>

      {/* Item Detail Dialog */}
      <Dialog
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${peerCampusTheme.border}`,
            overflow: 'hidden'
          }
        }}
      >
        {selectedItem && (
          <>
            <DialogTitle sx={{ 
              borderBottom: `1px solid ${peerCampusTheme.border}`,
              bgcolor: peerCampusTheme.surface
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="600" color={peerCampusTheme.text}>
                  {selectedItem.title}
                </Typography>
                <IconButton onClick={() => setSelectedItem(null)} size="small">
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            
            <DialogContent sx={{ p: 3, bgcolor: peerCampusTheme.surface }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={imageStyles.container}>
                    <img
                      src={selectedItem.image || "/fallback-image.jpg"}
                      alt={selectedItem.title}
                      style={imageStyles.img}
                      onError={(e) => {
                        e.target.src = "/fallback-image.jpg";
                      }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                      <Chip 
                        label={selectedItem.type.toUpperCase()} 
                        color={selectedItem.type === "lost" ? "error" : "success"}
                        sx={{ fontWeight: 'bold' }}
                      />
                      {selectedItem.status === "inactive" && (
                        <Chip 
                          label="INACTIVE" 
                          color="default"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                      {selectedItem.type === "lost" && selectedItem.urgency === "high" && (
                        <Chip label="URGENT" color="error" size="small" />
                      )}
                      {selectedItem.type === "lost" && selectedItem.reward && (
                        <Chip 
                          label="REWARD OFFERED" 
                          sx={{ 
                            bgcolor: peerCampusTheme.warning,
                            color: 'white'
                          }} 
                          size="small"
                        />
                      )}
                      {selectedItem.type === "found" && selectedItem.claimed && (
                        <Chip label="CLAIMED" color="success" size="small" />
                      )}
                    </Stack>
                    
                    <Chip 
                      label={selectedItem.category || "Other"} 
                      variant="outlined"
                      sx={{ 
                        borderColor: peerCampusTheme.primary,
                        color: peerCampusTheme.primary,
                        mb: 2
                      }}
                    />
                  </Box>
                  
                  <Typography variant="body1" color={peerCampusTheme.text} sx={{ mb: 3 }}>
                    {selectedItem.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn sx={{ color: peerCampusTheme.accent }} />
                      <Typography variant="body2" color={peerCampusTheme.text}>
                        {selectedItem.location}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email sx={{ color: peerCampusTheme.accent }} />
                      <Typography variant="body2" color={peerCampusTheme.text}>
                        {selectedItem.email}
                      </Typography>
                    </Box>
                    
                    {selectedItem.reward && selectedItem.rewardDetails && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CardGiftcard sx={{ color: peerCampusTheme.warning }} />
                        <Typography variant="body2" color={peerCampusTheme.text}>
                          Reward: {selectedItem.rewardDetails}
                        </Typography>
                      </Box>
                    )}
                    
                    <Typography variant="caption" color={peerCampusTheme.textSecondary}>
                      Posted on {formatDate(selectedItem.created_at)}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                      <FavoriteBorder sx={{ color: peerCampusTheme.textSecondary }} />
                      <Typography variant="body2" color={peerCampusTheme.textSecondary}>
                        {selectedItem.likes || 0} likes
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            
            <DialogActions sx={{ 
              p: 2, 
              borderTop: `1px solid ${peerCampusTheme.border}`,
              bgcolor: peerCampusTheme.surface
            }}>
              <Button 
                onClick={() => setSelectedItem(null)}
                sx={{ color: peerCampusTheme.textSecondary }}
              >
                Close
              </Button>
              <Button 
                variant="outlined"
                onClick={() => {
                  openEditDialog(selectedItem);
                  setSelectedItem(null);
                }}
                sx={{ 
                  borderColor: peerCampusTheme.primary,
                  color: peerCampusTheme.primary,
                  borderRadius: 2
                }}
              >
                Edit
              </Button>
              <Button 
                variant="contained"
                onClick={() => {
                  if (selectedItem.email) {
                    window.open(`mailto:${selectedItem.email}?subject=Regarding your ${selectedItem.type} item: ${selectedItem.title}`, '_blank');
                  }
                }}
                sx={{ 
                  borderRadius: 2,
                  background: peerCampusGradient,
                  '&:hover': {
                    background: peerCampusGradient,
                  }
                }}
              >
                Contact Owner
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog
        open={Boolean(editItem)}
        onClose={() => setEditItem(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${peerCampusTheme.border}`,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${peerCampusTheme.border}` }}>
          <Typography variant="h6" fontWeight="600" color={peerCampusTheme.text}>
            Edit {editItem?.type === "lost" ? "Lost" : "Found"} Item
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Title"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              fullWidth
              size="small"
              InputProps={{
                sx: { borderRadius: 1 }
              }}
            />
            
            <TextField
              label="Description"
              multiline
              rows={3}
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              fullWidth
              size="small"
              InputProps={{
                sx: { borderRadius: 1 }
              }}
            />
            
            <TextField
              label="Location"
              value={editForm.location}
              onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
              fullWidth
              size="small"
              InputProps={{
                sx: { borderRadius: 1 }
              }}
            />
            
            <TextField
              label="Category"
              value={editForm.category}
              onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
              fullWidth
              size="small"
              InputProps={{
                sx: { borderRadius: 1 }
              }}
            />
            
            {editItem?.type === "lost" && (
              <>
                <TextField
                  select
                  label="Urgency"
                  value={editForm.urgency}
                  onChange={(e) => setEditForm({ ...editForm, urgency: e.target.value })}
                  fullWidth
                  size="small"
                  InputProps={{
                    sx: { borderRadius: 1 }
                  }}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </TextField>
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editForm.reward}
                      onChange={(e) => setEditForm({ ...editForm, reward: e.target.checked })}
                    />
                  }
                  label="Offer reward for return"
                />
                
                {editForm.reward && (
                  <TextField
                    label="Reward Details"
                    value={editForm.rewardDetails}
                    onChange={(e) => setEditForm({ ...editForm, rewardDetails: e.target.value })}
                    fullWidth
                    size="small"
                    placeholder="e.g., $50, Gift card, etc."
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${peerCampusTheme.border}` }}>
          <Button 
            onClick={() => setEditItem(null)}
            sx={{ color: peerCampusTheme.textSecondary }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleEditItem}
            disabled={!editForm.title.trim()}
            sx={{ 
              borderRadius: 1,
              background: peerCampusGradient,
              '&:hover': {
                background: peerCampusGradient,
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${peerCampusTheme.border}`,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${peerCampusTheme.border}` }}>
          <Typography variant="h6" fontWeight="600" color={peerCampusTheme.text}>
            Delete Item
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Typography color={peerCampusTheme.text}>
            Are you sure you want to delete "{deleteConfirm?.title}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${peerCampusTheme.border}` }}>
          <Button 
            onClick={() => setDeleteConfirm(null)}
            sx={{ color: peerCampusTheme.textSecondary }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteItem}
            sx={{ borderRadius: 1 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={Boolean(deactivateConfirm)}
        onClose={() => setDeactivateConfirm(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${peerCampusTheme.border}`,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${peerCampusTheme.border}` }}>
          <Typography variant="h6" fontWeight="600" color={peerCampusTheme.text}>
            Deactivate Item
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Typography color={peerCampusTheme.text} gutterBottom>
            Are you sure you want to deactivate "{deactivateConfirm?.title}"?
          </Typography>
          <Typography variant="body2" color={peerCampusTheme.textSecondary}>
            The item will be hidden from other users but you can reactivate it later.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${peerCampusTheme.border}` }}>
          <Button 
            onClick={() => setDeactivateConfirm(null)}
            sx={{ color: peerCampusTheme.textSecondary }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            color="warning"
            onClick={handleDeactivateItem}
            sx={{ borderRadius: 1 }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Success Dialog */}
      <Dialog
        open={deactivateSuccess}
        onClose={() => setDeactivateSuccess(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${peerCampusTheme.border}`,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${peerCampusTheme.border}` }}>
          <Typography variant="h6" fontWeight="600" color={peerCampusTheme.success}>
            Success!
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: peerCampusTheme.success, mb: 2 }} />
          <Typography variant="h6" color={peerCampusTheme.text} gutterBottom>
            Item Deactivated Successfully
          </Typography>
          <Typography variant="body2" color={peerCampusTheme.textSecondary}>
            The item has been hidden from other users.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${peerCampusTheme.border}`, justifyContent: 'center' }}>
          <Button 
            variant="contained"
            onClick={() => setDeactivateSuccess(false)}
            sx={{ 
              borderRadius: 2,
              background: peerCampusGradient,
              '&:hover': {
                background: peerCampusGradient,
              }
            }}
          >
            Got it!
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Notifications */}
      <Snackbar
        open={!!notification.message}
        autoHideDuration={4000}
        onClose={() => setNotification({ message: "", type: "" })}
        anchorOrigin={isSmallMobile ? { vertical: "top", horizontal: "center" } : { vertical: "top", horizontal: "right" }}
        sx={{ top: { xs: 72, sm: 96, md: 112 } }}
      >
        <div>
          <EnhancedAlert 
            severity={notification.type} 
            message={notification.message}
            onClose={() => setNotification({ message: "", type: "" })}
          />
        </div>
      </Snackbar>

      {/* Success Message Dialog */}
      <Dialog
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${peerCampusTheme.border}`,
          }
        }}
      >
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: peerCampusTheme.success, mb: 2 }} />
          <Typography variant="h6" color={peerCampusTheme.text} gutterBottom>
            Success!
          </Typography>
          <Typography variant="body1" color={peerCampusTheme.textSecondary} sx={{ mb: 3 }}>
            {successMessage}
          </Typography>
          <Button
            variant="contained"
            onClick={() => setShowSuccess(false)}
            sx={{ 
              borderRadius: 2,
              background: peerCampusGradient,
              '&:hover': {
                background: peerCampusGradient,
              }
            }}
          >
            Continue
          </Button>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      {!embedded && (
        <div ref={footerRef}>
          <PostLoginFooter />
        </div>
      )}
    </Box>
  );
}